import {Injectable, Injector} from "@angular/core";
import {ReportError} from "app/app.service";
import {CommandExecutionDialogComponent} from "app/aws-ssm/automation-document/command-execution-dialog/command-execution-dialog.component";
import {DocumentExecutionDialogComponent} from "app/aws-ssm/automation-document/document-execution-dialog/document-execution-dialog.component";

import {BaseListService, BatchResponse} from "app/services/domain/base.service";
import * as Models from "app/services/proxy/model/models";
import {BaseComponent} from "framework/ui/components";

@Injectable()
export class AwsSsmDocumentService extends BaseListService<Models.AwsSsmListDocumentsRequest, Models.BatchRequest, Models.AwsSsmDocument>
{
    public versionDetails: VersionListBatchResponse = {
        listRecord : [],
        batchRecord: []
    };

    constructor(inj: Injector)
    {
        super(inj, Models.AwsSsmDocument);
    }

    @ReportError
    public list(listReq: Models.AwsSsmListDocumentsRequest): Promise<Models.ListResponse>
    {
        return this.domain.apis.awsSsm.documentList(this.app.domain.users.lastActiveTenantId, listReq);
    }

    @ReportError
    protected async getBatchResponseImpl(req: Models.BatchRequest): Promise<BatchResponse<Models.AwsSsmDocument>>
    {
        const recordIdBatchRequest = Models.RecordIdentityBatchRequest.newInstance({
                                                                                       ids: req?.ids.map((id) => Models.RecordIdentity.newInstance({
                                                                                                                                                       sysId: id
                                                                                                                                                   }))
                                                                                   });

        return this.domain.apis.awsSsm.documentBatch(this.app.domain.users.lastActiveTenantId, recordIdBatchRequest);
    }

    @ReportError
    public async getVersionList(documentId: string): Promise<VersionListBatchResponse>
    {
        this.versionDetails.listRecord  = await this.app.domain.apis.awsSsm.documentVersionsList(this.app.domain.users.lastActiveTenantId, documentId);
        const req                       = Models.RecordIdentityBatchRequest.newInstance({
                                                                                            ids: this.versionDetails.listRecord.map(versionRecord => Models.RecordIdentity.newInstance({
                                                                                                                                                                                           sysId: versionRecord.sysId

                                                                                                                                                                                       }))
                                                                                        });
        this.versionDetails.batchRecord = await this.getVersionBatch(documentId, req);
        return this.versionDetails;
    }

    @ReportError
    public async getVersionBatch(documentId: string,
                                 req: Models.RecordIdentityBatchRequest): Promise<Models.AwsSsmDocumentVersionDetails[]>
    {
        const versionBatch = await this.app.domain.apis.awsSsm.documentVersionsBatch(this.app.domain.users.lastActiveTenantId, documentId, req);
        return Object.values(versionBatch.results);
    }

    public async fetchFilters(documentType: DocumentType): Promise<Models.AwsSsmDocumentFilter>
    {
        return this.app.domain.apis.awsSsm.listDocumentFilters(this.app.domain.users.lastActiveTenantId, documentType);
    }

    public getVersionRecord(version: string): Models.AwsSsmDocumentVersionDetails
    {
        return this.versionDetails?.batchRecord.find(versionRecord => versionRecord.document_version === version);
    }

    public getCloudAccountName(cloud_account_id: string,
                               filterOptions: Models.AwsSsmDocumentFilter): string
    {
        const cloudAccObject: Models.AwsSsmCloudAccount = filterOptions?.cloud_account_details?.find(cloudAccount => cloudAccount.id === cloud_account_id);
        return cloudAccObject?.name || cloud_account_id;
    }

    public frameResReqBody(reqObj: ExecuteResourceInventoryPayload): Models.InventoryResourceDetailsRequest
    {
        const cloudAccountName = this.getCloudAccountName(reqObj.cloud_account_id, reqObj.filterOptions);
        const filterDetails = Models.InventoryResourceDetailsDataModel.newInstance({
                                                                                       cloud_account    : [
                                                                                           Models.CloudAccountDetail.newInstance(
                                                                                               {
                                                                                                   name             : cloudAccountName,
                                                                                                   id               : reqObj.cloud_account_id,
                                                                                                   preferred_regions: [reqObj.regions]
                                                                                               })
                                                                                       ],
                                                                                       service          : Models.CloudServiceName.AWS,
                                                                                       category         : reqObj.category,
                                                                                       component        : reqObj.component,
                                                                                       resource         : reqObj.resource,
                                                                                       operation_id     : reqObj.operation_id,
                                                                                       fetch_live_status: true
                                                                                   });
        return Models.InventoryResourceDetailsRequest.newInstance({
                                                                      filters: filterDetails
                                                                  });
    }

    public getCategory(type: ExecuteResourceValues): string
    {
        switch (type)
        {
            case ExecuteResourceValues.ResourceGroup:
                return "Management_Tools";

            case ExecuteResourceValues.Roles:
                return "Identity";

            case ExecuteResourceValues.Bucket:
                return "Storage";

            default:
                return "Compute";
        }
    }

    public getComponent(type: ExecuteResourceValues): string
    {
        switch (type)
        {
            case ExecuteResourceValues.ResourceGroup:
                return "Resource_Groups";

            case ExecuteResourceValues.Roles:
                return "IAM";

            case ExecuteResourceValues.Bucket:
                return "S3";

            default:
                return "EC2";
        }
    }

    public getResource(type: ExecuteResourceValues): string
    {
        switch (type)
        {
            case ExecuteResourceValues.ResourceGroup:
                return "Saved_Resource_Groups";

            case ExecuteResourceValues.Roles:
                return "IAM_Roles";

            case ExecuteResourceValues.Bucket:
                return "Buckets";

            default:
                return "Instances";
        }
    }

    public getOperation(type: ExecuteResourceValues): string
    {
        switch (type)
        {
            case ExecuteResourceValues.ResourceGroup:
                return "ListAwsResourceGroups";

            case ExecuteResourceValues.Roles:
                return "GetAccountAuthorizationDetailsRoles";

            case ExecuteResourceValues.Bucket:
                return "ListBuckets";

            default:
                return "DescribeInstances";
        }
    }

    public async fetchInventoryDetails(reqObj: ExecuteResourcePayload): Promise<Models.InventoryResourceDetailsModelList[]>
    {
        const type = reqObj.type;

        const requestPayload: ExecuteResourceInventoryPayload = {
            regions         : reqObj.region,
            cloud_account_id: reqObj.cloud_account_id,
            filterOptions   : reqObj.filterOptions,
            category        : this.getCategory(type),
            component       : this.getComponent(type),
            resource        : this.getResource(type),
            operation_id    : this.getOperation(type)
        };

        const inventoryReqBody  = this.frameResReqBody(requestPayload);
        const inventoryResponse = await this.app.domain.apis.resource.getInventoryDetails(this.app.domain.users.lastActiveTenantId, inventoryReqBody);
        return inventoryResponse.resource_details;
    }

    public async executeDialog(currentComponent: BaseComponent,
                               rowObj: Models.AwsSsmDocument,
                               filterOptions: Models.AwsSsmDocumentFilter,
                               type: DocumentType,
                               prefillObj?: Models.AwsSsmExecuteCommandDocumentRequest): Promise<void>
    {
        switch (type)
        {
            case DocumentType.Automation:
                await DocumentExecutionDialogComponent.open(currentComponent, new ExecuteDocumentState(this.app.domain.users.lastActiveTenantId, rowObj, filterOptions));
                break;
            case DocumentType.Command:
            default:
                await CommandExecutionDialogComponent.open(currentComponent, new ExecuteDocumentState(this.app.domain.users.lastActiveTenantId, rowObj, filterOptions, prefillObj));
                break;
        }
    }

    public getSvg(id: string): string
    {
        let path = "";
        switch (id)
        {
            case AwsSsmIconType.Danger:
                path = "assets/img/danger.svg";
                break;

            case AwsSsmIconType.Info:
            default:
                path = "assets/img/information-white.svg";
                break;
        }
        return path;
    }

    protected createBatchRequest(listReq: Models.AwsSsmListDocumentsRequest,
                                 ids: string[]): Models.BatchRequest
    {
        return Models.BatchRequest.newInstance({ids: ids});
    }
}

export class DownloadDisabledTooltipManager {
    private readonly m_maxCount: number = 50_000;

    constructor(private table: {count: number},
                private noDataMessage: string)
    {}

    get downloadDisabledReason(): string
    {
        if (!this.table.count) return this.noDataMessage || "No data is selected";

        if (this.table.count > this.m_maxCount) return `Too much data is selected. The download limit is ${this.m_maxCount} records`;

        return null;
    }

    get canDownload(): boolean
    {
        return this.table.count > 0 && this.table.count <= this.m_maxCount;
    }
}

export enum AwsSsmIconType
{
    Info   = "Info",
    Danger = "Danger"
}

export enum AwsSsmRedirectionScope
{
    AwsSsm              = "aws-ssm",
    AutomationExecution = "automation-execution",
    CommandExecution    = "command-execution"
}

export enum DocumentType
{
    Automation = "Automation",
    Command    = "Command"
}

export enum ConcurrencyOptions
{
    Targets    = "Targets",
    Percentage = "Percentage"
}

export enum ErrorOptions
{
    Errors     = "Errors",
    Percentage = "Percentage"
}

export interface VersionListBatchResponse
{
    listRecord: Models.AwsSsmListDocumentVersion[],
    batchRecord: Models.AwsSsmDocumentVersionDetails[];
}

export interface TagObject
{
    key: string;
    value: string;
}

export interface InputParametersState
{
    formattedModelValue: string;
    currentModelValue: string;
    valid: boolean;
}

export enum ExecuteResourceValues
{
    Bucket        = "Bucket",
    ResourceGroup = "ResourceGroup",
    Roles         = "Roles",
    Instance      = "Instance"
}

export interface ExecuteResourcePayload
{
    type: ExecuteResourceValues;
    region: string;
    cloud_account_id: string;
    filterOptions: Models.AwsSsmDocumentFilter;
}

interface ExecuteResourceInventoryPayload
{
    category: string;
    cloud_account_id: string;
    filterOptions: Models.AwsSsmDocumentFilter;
    regions: string;
    component: string;
    resource: string;
    operation_id: string;
}

export class ExecuteDocumentState
{
    constructor(public readonly tenantId: string,
                public readonly initial: Models.AwsSsmDocument,
                public readonly filterValues: Models.AwsSsmDocumentFilter,
                public readonly prefillObj?: Models.AwsSsmExecuteCommandDocumentRequest)
    {}
}
